// ─────────────────────────────────────────────────────────────────────────────
// WebMCP stub — in-browser agents call the SAME capability layer (roadmap §1d)
// ─────────────────────────────────────────────────────────────────────────────
// Progressive enhancement: where the browser exposes `navigator.modelContext`
// (WebMCP origin trial), we register the read capabilities as tools whose
// handlers just POST to the agent-api REST route — ZERO logic in the browser
// layer, the same deterministic edge handler every transport reaches. Where the
// API is absent (today's browsers, SSR, tests) this is a silent no-op: the
// feature check must never throw (acceptance: "WebMCP stub feature-detection
// zero errors").
//
// The tool descriptor here mirrors the edge capability registry
// (supabase/functions/agent-api/capabilities.ts). webmcp.test.ts cross-imports
// both and fails if the tool name / required inputs drift — so the browser tool
// can never silently diverge from the server contract.

/** One WebMCP tool descriptor — kept minimal and drift-locked against the registry. */
export interface WebMcpTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: unknown) => Promise<unknown>;
}

/** The agent-api REST base in the browser (Supabase edge functions origin).
 *  Accessed as a direct member expression so Vite statically inlines it (the
 *  same form src/lib/supabase.ts uses). */
export function agentApiBase(): string {
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  return url ? `${url}/functions/v1/agent-api` : "/agent-api";
}

/** Build the Phase-1 tool set. searchKnowledge only (reads). */
export function buildTools(base = agentApiBase()): WebMcpTool[] {
  return [
    {
      name: "searchKnowledge",
      description:
        "Search Think Forward's published Canadian-immigration knowledge and return entries verbatim with official-source citations. Read-only; general information, not immigration advice.",
      inputSchema: {
        type: "object",
        additionalProperties: false,
        properties: {
          query: { type: "string" },
          lang: { type: "string", enum: ["en", "zh"] },
          limit: { type: "integer", minimum: 1, maximum: 20 },
        },
        required: ["query"],
      },
      execute: async (args: unknown) => {
        const res = await fetch(`${base}/searchKnowledge`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(args ?? {}),
        });
        return await res.json();
      },
    },
  ];
}

/** Minimal structural view of the WebMCP surface we use (not in TS DOM lib). */
interface ModelContextLike {
  registerTool?: (tool: WebMcpTool) => unknown;
  provideContext?: (ctx: { tools: WebMcpTool[] }) => unknown;
}

/**
 * Register the capability tools if the browser supports WebMCP. Returns the
 * number of tools registered (0 = unsupported / no-op). NEVER throws — any
 * failure is caught and reported as 0 so a missing/al­tered API can't break app
 * startup.
 */
export function registerAgentWebMCP(): number {
  try {
    if (typeof navigator === "undefined") return 0;
    const mc = (navigator as unknown as { modelContext?: ModelContextLike }).modelContext;
    if (!mc) return 0;
    const tools = buildTools();
    if (typeof mc.registerTool === "function") {
      for (const t of tools) mc.registerTool(t);
      return tools.length;
    }
    if (typeof mc.provideContext === "function") {
      mc.provideContext({ tools });
      return tools.length;
    }
    return 0;
  } catch {
    // Progressive enhancement: a WebMCP failure must never surface to the user.
    return 0;
  }
}
